import { CSSProperties, ReactNode } from "react";
import clsx from "clsx";
// MUI
import { ButtonBase, Grid, Skeleton, Stack, Typography } from "@mui/material";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";

// Helpers
import { and, or } from "@Common/helpers/sonar";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

// Components
import DateDiff from "@Layouts/components/DateDiff";

// Types
import { IUser } from "@Common/interfaces/user";

type PayPlanInfo = {
	payPlanId: string;
	payPlanName: string;
	assignmentDate: string | Date | null;
	acknowledgementDate: string | Date | null;
};

type AssignmentStatusCardSectionProp = Readonly<{
	employee: IUser;
	assignmentPhase: "Current" | "Upcoming";
	isLoading: boolean;
	isDisabled: boolean;
	selectedList: string[];
	payPlanInfo: Partial<PayPlanInfo>;
	// Callbacks
	onClickPayPlan: (planId: string) => void;
}>;

type CardTextProps = Readonly<{
	children: ReactNode;
	isDisabled: boolean;
	fontFamily?: "sans" | "montserrat";
	isError?: boolean;
	isBold?: boolean;
	sx?: CSSProperties;
}>;
function CardText(props: CardTextProps) {
	const { fontFamily = "sans", isDisabled = false, isError = false, isBold = false } = props;
	return (
		<Typography
			className={clsx({
				"!text-[14.5px]": true,
				"!text-default": !isDisabled && !isError,
				"!font-sans": fontFamily === "sans",
				"!font-montserrat": fontFamily === "montserrat",
				"!font-normal ": !isBold,
				"!font-bold": isBold,
				"!text-disabled": isDisabled && !isError,
				"!text-alert": isError && !isDisabled,
			})}
			sx={props.sx}
		>
			{props.children}
		</Typography>
	);
}

export default function AssignmentStatusCardSection(props: AssignmentStatusCardSectionProp) {
	const { employee, isLoading, isDisabled, selectedList, assignmentPhase, payPlanInfo, onClickPayPlan } = props;
	const { payPlanId, payPlanName, assignmentDate, acknowledgementDate } = payPlanInfo;

	const { timezone } = useLocation() ?? {};

	const existPayPlan = and(!!payPlanId, !!payPlanName);

	return (
		<Stack>
			<Grid container direction={"column"}>
				<Grid
					item
					xs={12}
					sx={{
						display: "flex",
						columnGap: 0.3,
					}}
				>
					<CardText isDisabled={!existPayPlan} isBold>
						{assignmentPhase}:
					</CardText>
					{isLoading && selectedList.includes(employee._id as string) ? (
						<Skeleton classes={{ root: "rounded" }} variant="rectangular" width={80} />
					) : (
						<ButtonBase
							classes={{
								root: "!font-sans !font-bold !text-[#666666] !text-[14.5px]",
								disabled: "!text-disabled",
							}}
							disabled={or(isDisabled, !existPayPlan)}
							onClick={() => onClickPayPlan(payPlanId as string)}
						>
							{payPlanName ?? `No ${assignmentPhase} Pay Plan`}
						</ButtonBase>
					)}
				</Grid>
				<Grid
					item
					xs={12}
					sx={{
						display: "flex",
						columnGap: 0.3,
					}}
				>
					<CardText isDisabled={and(!existPayPlan, !assignmentDate)}>Assigned:</CardText>
					{isLoading && selectedList.includes(employee._id as string) ? (
						<Skeleton classes={{ root: "rounded" }} variant="rectangular" width={130} />
					) : (
						<Stack direction="row" alignItems={"flex-start"}>
							<CardText
								sx={{
									marginRight: 0.5,
									width: "115px",
								}}
								isDisabled={and(!existPayPlan, !assignmentDate)}
							>
								{assignmentDate ? getFormattedDate(assignmentDate, timezone, DATE_FORMAT.monthAbbrDatePadded) : ""}
							</CardText>
							{assignmentDate && (
								<DateDiff
									maxWidth="100px"
									containerProps={{
										marginTop: "-1px",
									}}
									date={assignmentDate}
									classNameClock="!text-neutral-gray"
									color="#B3B3B3"
								/>
							)}
						</Stack>
					)}
				</Grid>
				<Grid
					xs={12}
					sx={{
						display: "flex",
						columnGap: 0.3,
						alignItems: "center",
						marginBottom: 1,
					}}
					item
				>
					<CardText isDisabled={!existPayPlan} isError={and(existPayPlan, !acknowledgementDate)}>
						Acknowledged:
					</CardText>

					{acknowledgementDate ? (
						<CardText isDisabled={!existPayPlan}>
							{getFormattedDate(acknowledgementDate, timezone, DATE_FORMAT.monthAbbrDatePadded)}
						</CardText>
					) : (
						<CardText isError={true} isDisabled={false}>
							{and(!acknowledgementDate) ? "Not Acknowledged Yet" : ""}
						</CardText>
					)}
				</Grid>
			</Grid>
		</Stack>
	);
}
